<template>
  <div
    :id="'sb-combobox-wrapper-' + uid"
    v-click-outside="hideList"
    class="sb-multiselect"
    :class="{
      'sb-multiselect--open': isOpen,
      'sb-multiselect--loading': isLoading,
    }"
  >
    <SbSelectInner
      ref="inner"
      :style="`anchor-name: --dialog-${uid};`"
      :uid="uid"
      :allow-create="allowCreate"
      :clearable="clearable"
      :data-testid="`${dataTestid}-inner`"
      :emit-option="emitOption"
      :error="error"
      :warning="warning"
      :input-id="inputId"
      :is-disabled="isDisabled"
      :is-loading="isLoading"
      :is-open="isOpen"
      :item-caption="itemCaption"
      :item-label="itemLabel"
      :item-value="itemValue"
      :placeholder="placeholder.length ? placeholder : label"
      :left-icon="leftIcon"
      :left-icon-color="leftIconColor"
      :loading-label="loadingLabel"
      :model-value="modelValue"
      :options="transformedOptions"
      :search-input="searchInput || ''"
      :show-all-filter-options="showAllFilterOptions"
      :show-caption="showCaption"
      :use-avatars="useAvatars"
      :disable-filter="disableFilter"
      :helper-text="helperText"
      @clear-all-values="handleClearAllValues"
      @click="handleDropdownDisplay"
      @close-list="hideList"
      @emit-value="handleEmitValue"
      @keydown-enter="handleKeyDownEnter"
      @remove-item-value="handleRemoveItemValue"
      @search="handleSearchInput"
      @show-all-options="handleShowAllFilterOptions"
      @show-list="showList"
      @focus-at-index="focusAtIndexByLabel"
    >
      <template #selection="scope">
        <slot name="selection" v-bind="scope" />
      </template>

      <template #rightIcon>
        <slot name="rightIcon" />
      </template>

      <slot name="innerSelect" />
    </SbSelectInner>

    <span
      v-if="helperText"
      :id="`sb-combobox-${uid}-helper-text`"
      class="sb-select__message sb-select__message--helper"
    >
      {{ helperText }}
    </span>

    <span
      v-if="showErrorMessage"
      class="sb-multiselect__message sb-multiselect__message--error"
    >
      {{ errorMessage }}
    </span>

    <SbSelectList
      v-if="isListRendered"
      ref="list"
      v-infinite-scroll="handleInfiniteScroll"
      :allow-create="allowCreate"
      :data-testid="`${dataTestid}-list`"
      :emit-option="emitOption"
      :input-id="uid"
      :is-loading-more="isLoadingMore"
      :is-loading="isLoading"
      :is-option-disabled="isOptionDisabled"
      :item-caption="itemCaption"
      :item-label="itemLabel"
      :item-value="itemValue"
      :loading-more-text="loadingMoreText"
      :model-value="modelValue"
      :no-data-text-tag="noDataTextTag"
      :no-data-text="noDataText"
      :options="filteredOptions"
      :search-input="searchInput"
      :select-all-visible="selectAllVisible"
      :show-caption="showCaption"
      :use-avatars="useAvatars"
      @emit-value="handleEmitValue"
      @option-created="handleOptionCreated"
      @focus-item="focusAtIndex($event)"
      @select-all="handleSelectAll"
    >
      <template #list-item="scope">
        <slot name="list-item" v-bind="scope" />
      </template>
    </SbSelectList>

    <slot name="minibrowser" />
  </div>
</template>

<script lang="ts">
import { ref, watch, type PropType } from 'vue'
import { debounce } from 'throttle-debounce'
import { ClickOutside, InfiniteScroll } from '../../directives'
import type { IconColor } from '../Icon/types'
import { canUseDOM, includes, toLowerCase } from '../../utils'

import { getSearchTextFromSource } from '../Select/helpers'
import SbSelectInner from './components/SelectInner.vue'
import SbSelectList from './components/SelectList.vue'
import { type MultiSelectValue, type Option } from './types'

export default {
  name: 'SbMultiSelect',

  directives: {
    ClickOutside,
    InfiniteScroll,
  },

  components: {
    SbSelectInner,
    SbSelectList,
  },

  provide() {
    return {
      selectContext: () => this.selectContext,
    }
  },

  props: {
    // shared props with SbSelect
    /**
     * Defines which item is selected
     * @type {string[]|number[]}
     */
    modelValue: {
      type: Array as PropType<MultiSelectValue[]>,
      default: null,
    },

    /**
     * Defines the debounce time to filter the options
     */
    filterDebounce: {
      type: Number,
      default: 800,
    },

    /**
     * Defines if the select is disabled
     */
    isDisabled: Boolean,

    /**
     * Defines if the select should emit the option object. See the "Emit Option" section for more details
     */
    emitOption: Boolean,

    /**
     * Defines if the select is loading
     */
    isLoading: Boolean,

    /**
     * Defines the loading label text
     */
    loadingLabel: {
      type: String,
      default: 'Loading...',
    },

    /**
     * Defines the placeholder text
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * @ignore
     * [DEPRECATED] Defines the label of the select
     */
    label: {
      // being deprecated, only here for backward compatibility
      type: String,
      default: '',
    },

    /**
     * Defines the left icon of the select
     */
    leftIcon: {
      type: String,
      default: null,
    },

    /**
     * Defines the color of the left icon
     */
    leftIconColor: {
      type: String as PropType<IconColor>,
      default: null,
    },

    /**
     * Defines the options of the select. See the "Options" section for more details
     */
    options: {
      type: Array as PropType<Option[] | string[]>,
      default: () => [],
    },

    /**
     * Defines if the select will use avatars
     */
    useAvatars: Boolean,

    /**
     * Defines the label key of the options. See "Configuring Options" section for more details
     */
    itemLabel: {
      type: String,
      default: 'label',
    },

    /**
     * Defines the value key of the options. See "Configuring Options" section for more details
     */
    itemValue: {
      type: String,
      default: 'value',
    },

    /**
     * Defines the text when no data is found
     */
    noDataText: {
      type: String,
      default: 'Sorry, no result found.',
    },

    /**
     * Defines the error message
     */
    errorMessage: {
      type: String,
      default: '',
    },

    /**
     * Defines if the select has an error
     */
    error: Boolean,

    /**
     * Defines if the select has a warning state
     */
    warning: Boolean,

    /**
     * Defines if the select should show the caption
     */
    showCaption: Boolean,

    /**
     * Defines the caption key of the options. See "Configuring Options" section for more details
     */
    itemCaption: {
      type: String,
      default: 'caption',
    },

    /**
     * Defines if the select should use the infinite scroll. When this is enabled, the load-more event will be emitted when the user scrolls to the end of the list. See the "Infinite Scroll" section for more details
     */
    infiniteScroll: Boolean,

    /**
     * Defines if the select is loading more options. See the "Infinite Scroll" section for more details
     */
    isLoadingMore: Boolean,

    /**
     * Defines the text when the select is loading more options
     */
    loadingMoreText: {
      type: String,
      default: 'Loading more...',
    },

    renderOnOpen: Boolean,

    /**
     * Defines if a select item in the list should be disabled. See the "Configuring Options" section for more details
     */
    isOptionDisabled: {
      type: Function,
      default: () => false,
    },

    /**
     * Make the input readonly disabling the filter
     */
    disableFilter: Boolean,

    /**
     * Disables the internal filter functionality of the select
     */
    disableInternalFilter: Boolean,

    /**
     * Defines if the select could be clearable. When it is true, a clear icon button will be shown in the select
     */
    clearable: Boolean,

    /**
     * Defines if the select should be inline
     */
    inline: Boolean,

    /**
     * Defines the accessible element that labels the select
     */
    labeledBy: {
      type: String,
      default: '',
    },

    /**
     * Defines the ID for the input in SelectInner component
     */
    inputId: {
      type: String,
      default: undefined,
    },

    /**
     * Defines a helper text message. This will be shown below the select component
     */
    helperText: {
      type: String,
      default: undefined,
    },

    // SbMultiSelect only props
    /**
     *
     */
    selectAllVisible: Boolean,

    /**
     * Allow the creation of new options
     */
    allowCreate: Boolean,

    /**
     * Text to show when no data is found
     */
    noDataTextTag: {
      type: String,
      default: 'Start typing to add new tag.',
    },
  },

  emits: [
    'filter',
    'clear',
    'hide',
    'update:modelValue',
    'load-more',
    'option-created',
    'search-input',
    'show',
  ],

  setup(props, { emit }) {
    const showAllFilterOptions = ref(true)
    const searchInput = ref<string | undefined>(undefined)
    const handleSearchInput = (value: string | undefined) => {
      if (value === searchInput.value) {
        return
      }
      searchInput.value = value
    }

    watch(
      searchInput,
      debounce(props.filterDebounce, () => {
        const value = !showAllFilterOptions.value ? searchInput.value : ''
        emit('search-input', value)
        emit('filter', value)
      }),
    )

    return {
      showAllFilterOptions,
      searchInput,
      handleSearchInput,
    }
  },

  data: () => ({
    isOpen: false,
    activeIndex: -1,
    listItems: [] as unknown as NodeList,
    innerElement: null,
    vcoConfig: {
      detectIFrame: false,
    },
  }),

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-multiselect'
    },

    selectContext() {
      return {
        // controls elements
        activeIndex: this.activeIndex,
        listItems: this.listItems,

        // methods to control the menu state
        hideList: this.hideList,
        showList: this.showList,
        focusAtIndex: this.focusAtIndex,
        focusOnFirstItem: this.focusOnFirstItem,
        focusOnLastItem: this.focusOnLastItem,
      }
    },

    useMinibrowser() {
      return this.$slots.minibrowser && this.innerElement
    },

    isListRendered() {
      const shouldRender = this.renderOnOpen ? this.isOpen : true
      return !this.useMinibrowser && shouldRender
    },

    filteredOptions() {
      if (this.hasValueToSearch && !this.disableFilter) {
        return this.transformedOptions.filter((opt) => {
          return (
            this.searchInput &&
            includes(
              toLowerCase(opt[this.itemLabel] as string),
              toLowerCase(this.searchInput),
            )
          )
        })
      }

      return this.transformedOptions
    },

    hasValueToSearch() {
      return this.searchInput && this.searchInput.length > 0
    },

    hasInnerSlot() {
      return !!this.$slots.innerSelect
    },

    transformedOptions() {
      return this.options.map((opt) => {
        if (typeof opt === 'object') return opt
        return { [this.itemLabel]: opt, [this.itemValue]: opt }
      })
    },

    showErrorMessage() {
      return this.error && this.errorMessage
    },

    uid() {
      return this.$.uid.toString()
    },
  },

  watch: {
    activeIndex() {
      this.$_updateNavigation()
    },

    filteredOptions() {
      this.$nextTick(() => {
        this.$_loadListItems()
      })
    },
  },

  mounted() {
    this.$_loadListItems()

    this.$nextTick(() => {
      if (this.$refs.inner) {
        this.innerElement = (this.$refs.inner as typeof SbSelectInner).$el
      }
    })
  },

  methods: {
    handleShowAllFilterOptions(value: boolean) {
      this.showAllFilterOptions = value
    },
    /**
     * shows the items list
     */
    showList(shouldEmit = true) {
      if (this.isOpen) return

      this.isOpen = true

      if (shouldEmit) {
        this.$emit('show')
      }
      this.$nextTick(() => {
        canUseDOM &&
          this.$refs.inner &&
          (this.$refs.inner as typeof SbSelectInner).$el
            .querySelector('.sb-multiselect-inner__input')
            ?.focus()
      })
    },

    /**
     * hides the items list
     */
    hideList() {
      if (!this.isOpen) return

      this.isOpen = false
      this.$emit('hide')

      this.resetSearchOnHide()
    },

    /**
     * reset the search input value on select list hide
     */
    resetSearchOnHide() {
      this.$nextTick(() => {
        this.searchInput =
          getSearchTextFromSource(this.modelValue, {
            label: this.itemLabel,
            value: this.itemValue,
            options: this.transformedOptions,
          }) ?? ''
      })
    },

    /**
     * shows or hides the items list according to internal state
     */
    handleDropdownDisplay() {
      if (this.isDisabled || this.isLoading) return
      if (this.isOpen) {
        this.hideList()
      } else {
        this.showList()
      }
    },

    /**
     * emits the input event to make this component compatible with v-model directive
     * @param {Array<String>|String} value
     */
    handleEmitValue(value: any) {
      const emptyValue = ''
      const valueExists = this.transformedOptions.find(
        (option) =>
          option === value ||
          option[this.itemValue] === value ||
          option[this.itemLabel] === value,
      )
      if (valueExists) {
        const parsedValue = this.emitOption ? valueExists : value
        const inputValue = this.processMultipleValue(parsedValue)
        this.$emit('update:modelValue', inputValue)
      } else if (this.allowCreate) {
        this.handleOptionCreated(value)
      }
      this.searchInput = emptyValue
    },

    handleSelectAll() {
      const allSelected = this.transformedOptions.every((option) =>
        this.modelValue.includes(option[this.itemValue]),
      )
      if (allSelected) {
        this.$emit('update:modelValue', [])
      } else {
        this.$emit(
          'update:modelValue',
          this.transformedOptions.map((option) => option[this.itemValue]),
        )
      }
    },

    /**
     * emits the input event to make this component compatible with v-model directive
     * @param {Array<String>|String} value
     */
    handleOptionCreated(value: string | number) {
      this.searchInput = ''
      this.$emit('option-created', value)
      this.$_focusInner()
      this.hideList()
    },

    /**
     * check if the value exists on this.modelValue
     * but, based on this.emitOption, to check more
     * properly the case when this.modelValue is an array
     * of objects
     * @param  {String|Number|Object} value
     * @return {Boolean}
     */
    valueAlreadyExists(value: any) {
      if (this.emitOption) {
        const itemValue = value[this.itemValue]

        return (this.modelValue as unknown[])?.some(
          ($v: any) => $v[this.itemValue] === itemValue,
        )
      }

      return includes(this.modelValue, value)
    },

    /**
     * remove a specific value from this.modelValue, based on this.emitOption,
     * to check more properly the case when this.modelValue is an array
     * of objects
     * @param  {String|Number|Object} value
     * @return {Array}
     */
    removeValueFromArray(value: any) {
      if (this.emitOption && Array.isArray(this.modelValue)) {
        return this.modelValue.filter(
          (val: any) => val[this.itemValue] !== value[this.itemValue],
        )
      }

      if (Array.isArray(this.modelValue)) {
        return this.modelValue.filter((val: any) => val !== value)
      }

      return []
    },

    /**
     * returns the processed value to input event
     * @param {String} value
     */
    processMultipleValue(value: any) {
      if (this.valueAlreadyExists(value)) {
        return this.removeValueFromArray(value)
      }

      if (Array.isArray(this.modelValue)) {
        return [...this.modelValue, value]
      }

      return [value]
    },

    /**
     * emit an input event with the item from tag that was removed
     * @param {String} itemValue
     */
    handleRemoveItemValue(itemValue: any) {
      const $value = this.processMultipleValue(itemValue)
      this.$emit('update:modelValue', $value)
      this.$_focusInner()
    },

    /**
     * emit an input event to clear all selected values when multiple selection
     * or an empty string when single selection
     */
    handleClearAllValues() {
      this.$emit('clear')
      this.$emit('update:modelValue', [])
      this.hideList()
      this.$_focusInner()
    },

    /**
     * set focus to first list item
     */
    focusOnFirstItem() {
      this.showList()

      this.activeIndex = 0

      this.$_updateNavigation()
    },

    /**
     * set focus to last list item
     */
    focusOnLastItem() {
      this.showList()

      this.activeIndex = this.listItems.length - 1
    },

    /**
     * set focus a specific list item index
     * @param {Number} index Position index of menu list item
     */
    focusAtIndex(index: number) {
      this.$_updateTabIndex(index)

      this.activeIndex = index
    },

    /**
     * updates tab index for list items and
     * scroll to focused list item.
     * @param {Number} index Position index of menu list item
     */
    $_updateTabIndex(index: number) {
      if (this.listItems.length > 0) {
        const nodeAtIndex = this.listItems[index]

        this.listItems.forEach((node) => {
          if (node !== nodeAtIndex) {
            ;(node as HTMLElement).setAttribute('tabindex', '-1')
          }
        })
        if (!nodeAtIndex) {
          return
        }
        ;(nodeAtIndex as HTMLElement).setAttribute('tabindex', '0')
      }
    },

    /**
     * set all list items to tabindex -1
     */
    $_resetTabIndex() {
      if (this.listItems.length > 0) {
        this.listItems.forEach((node) => {
          ;(node as HTMLElement).setAttribute('tabindex', '-1')
        })
      }
    },

    /**
     * set focus to trigger button element
     */
    $_focusInner() {
      this.$nextTick(() => {
        const { $el, $refs } = this.$refs.inner as typeof SbSelectInner
        if (canUseDOM && $el instanceof HTMLElement) {
          $el.focus()
          $refs.search?.focus()
        }
      })
    },

    /**
     * get all list item elements
     */
    $_loadListItems() {
      if (!this.useMinibrowser) {
        const menuNode = canUseDOM && (this.$refs?.list as any)?.$el

        if (menuNode instanceof HTMLElement) {
          this.listItems = menuNode.querySelectorAll('li')
        }
      }
    },

    /**
     * force update navigation index
     */
    $_updateNavigation() {
      if (this.activeIndex !== -1) {
        this.$nextTick(() => {
          this.listItems[this.activeIndex] &&
            (this.listItems[this.activeIndex] as HTMLElement).focus()

          this.$_updateTabIndex(this.activeIndex)
        })

        return
      }

      this.$nextTick(() => {
        this.$_focusInner()

        this.$_resetTabIndex()
      })
    },

    /**
     * handle with keydown enter event from inner elemen
     * trying to select the first item in the array with single one item
     * or open the list if the list is not open
     */
    handleKeyDownEnter() {
      if (!this.isOpen) {
        this.showList()

        this.activeIndex = 0
        return
      }

      if (this.filteredOptions.length === 1) {
        this.handleEmitValue(this.filteredOptions[0][this.itemValue])
      }

      this.$_focusInner()
      this.hideList()
    },

    /**
     * emit a load-more event when the user scroll till the end of the list of items
     */
    handleInfiniteScroll() {
      if (this.infiniteScroll) {
        this.$emit('load-more')
      }
    },

    focusAtIndexByLabel(item: Option) {
      let index = this.filteredOptions.findIndex(
        (option) => option[this.itemLabel] === item[this.itemLabel],
      )

      if (this.selectAllVisible) {
        index += 1
      }

      if (index !== -1) {
        this.focusAtIndex(index)
      }
    },
  },
}
</script>
